<script>
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { getApiUrl } from '../api';
  import { selectedCountry } from '../countryStore';
  import { t } from '../i18nStore';
  import { calculateTaxRebate, normalizeTaxPromotion, getBankOrPlatformGroup, formatPromoSubtitle } from '../services/taxCalculator';
  import TaxRebateShareCard from '../components/TaxRebateShareCard.svelte';

  let totalTax = 60000;
  let preference = 'cash'; // 'cash' | 'miles'
  let crossPromotion = true;
  let enableInstallments = false;

  let installments = [
    { amount: 35000, dueDate: getDateInDays(30) },
    { amount: 25000, dueDate: getDateInDays(60) }
  ];

  let promotions = [];
  let selectedPromoIds = new Set();
  let isLoadingPromos = false;
  let calculationResult = null;

  function getDateInDays(days) {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  }

  onMount(async () => {
    await loadPromotions();
  });

  async function loadPromotions() {
    isLoadingPromos = true;
    try {
      const country = ($selectedCountry === 'Hong Kong' || $selectedCountry === 'HK') ? 'HK' : ($selectedCountry || 'HK');
      const url = getApiUrl(`/promotions/search?includeExpired=false&categories=Tax+Rebate&countryCode=${country}&page=1&pageSize=20`);
      const res = await fetch(url);
      let items = [];
      if (res.ok) {
        const json = await res.json();
        items = json.data || [];
      }

      if (items.length > 0) {
        promotions = items.map(p => normalizeTaxPromotion(p));
      } else {
        promotions = [];
      }
    } catch (e) {
      promotions = [];
    } finally {
      selectedPromoIds = new Set();
      isLoadingPromos = false;
    }
  }

  $: installmentsSum = installments.reduce((acc, row) => acc + (Number(row.amount) || 0), 0);
  $: isInstallmentSumValid = !enableInstallments || Math.abs(installmentsSum - totalTax) < 0.01;
  $: canCalculate = totalTax > 0 && selectedPromoIds.size > 0 && isInstallmentSumValid && !isLoadingPromos;

  $: sameBankWarnings = (() => {
    if (selectedPromoIds.size < 2) return [];
    const bankPromoMap = new Map();
    for (const id of selectedPromoIds) {
      const p = promotions.find(item => item.id === id);
      if (p) {
        const grp = getBankOrPlatformGroup(p);
        if (!bankPromoMap.has(grp)) bankPromoMap.set(grp, []);
        bankPromoMap.get(grp).push(p);
      }
    }
    const list = [];
    for (const [grp, pList] of bankPromoMap.entries()) {
      if (pList.length > 1) {
        list.push($t('tax.same_bank_warning', { bank: grp }));
      }
    }
    return list;
  })();

  function togglePromo(id) {
    if (selectedPromoIds.has(id)) {
      selectedPromoIds.delete(id);
    } else {
      selectedPromoIds.add(id);
    }
    selectedPromoIds = new Set(selectedPromoIds);
  }

  function selectAllPromos() {
    selectedPromoIds = new Set(promotions.map(p => p.id));
  }

  function clearAllPromos() {
    selectedPromoIds = new Set();
  }

  function splitInstallmentsEqually(count) {
    if (count <= 0) return;
    const base = Math.floor(totalTax / count);
    const rem = totalTax - (base * count);
    installments = Array.from({ length: count }, (_, i) => ({
      amount: (i === count - 1) ? base + rem : base,
      dueDate: getDateInDays(30 * (i + 1))
    }));
  }

  function addInstallmentRow() {
    installments = [
      ...installments,
      { amount: 0, dueDate: getDateInDays(30 * (installments.length + 1)) }
    ];
  }

  function removeInstallmentRow(index) {
    installments = installments.filter((_, i) => i !== index);
  }

  function handleCalculate() {
    const selected = promotions.filter(p => selectedPromoIds.has(p.id));
    calculationResult = calculateTaxRebate({
      totalTax: Number(totalTax) || 0,
      installments: enableInstallments ? installments : [],
      selectedPromotions: selected,
      crossPromotion,
      preference
    });
  }
</script>

<div class="calculator-page">
  <!-- Top Banner -->
  <div class="header-banner">
    <div class="banner-icon">✉️</div>
    <div class="banner-texts">
      <h2>{$t('tax.banner_title')}</h2>
      <p>{$t('tax.banner_subtitle')}</p>
    </div>
  </div>

  <div class="card-section">
    <h3>{$t('tax.section1_title')}</h3>
    <div class="preference-grid">
      <button
        class="pref-btn"
        class:active={preference === 'cash'}
        on:click={() => { preference = 'cash'; if (calculationResult) handleCalculate(); }}
      >
        <span class="icon">💰</span>
        <span class="title">{$t('tax.pref_cash')}</span>
        <span class="desc">{$t('tax.pref_cash_desc')}</span>
      </button>

      <button
        class="pref-btn"
        class:active={preference === 'miles'}
        on:click={() => { preference = 'miles'; if (calculationResult) handleCalculate(); }}
      >
        <span class="icon">✈️</span>
        <span class="title">{$t('tax.pref_miles')}</span>
        <span class="desc">{$t('tax.pref_miles_desc')}</span>
      </button>
    </div>
  </div>

  <div class="card-section">
    <h3>{$t('tax.section2_title')}</h3>
    <div class="input-group">
      <span class="currency-prefix">HK$</span>
      <input
        type="number"
        class="tax-input"
        bind:value={totalTax}
        min="0"
        step="1000"
        placeholder="60,000"
      />
    </div>
    <div class="preset-chips">
      {#each [20000, 50000, 60000, 100000, 200000] as preset}
        <button
          class="chip-btn"
          on:click={() => { totalTax = preset; if (enableInstallments && installments.length === 2) splitInstallmentsEqually(2); }}
        >
          HK$&nbsp;{preset.toLocaleString()}
        </button>
      {/each}
    </div>
  </div>

  <div class="card-section">
    <div class="section-header-row">
      <h3>{$t('tax.section3_title')}</h3>
      <label class="switch-container">
        <input type="checkbox" bind:checked={enableInstallments} on:change={() => { if (enableInstallments && installments.length === 0) splitInstallmentsEqually(2); }} />
        <span class="switch-label">{$t('tax.pay_installments')}</span>
      </label>
    </div>

    {#if enableInstallments}
      <div class="installments-box">
        <div class="inst-controls">
          <span class="inst-subtitle">{$t('tax.installment_schedule_subtitle')}</span>
          <div class="inst-actions">
            <button class="small-btn" on:click={() => splitInstallmentsEqually(2)}>{$t('tax.split_2')}</button>
            <button class="small-btn" on:click={() => splitInstallmentsEqually(3)}>{$t('tax.split_3')}</button>
            <button class="small-btn add" on:click={addInstallmentRow}>{$t('tax.add_installment')}</button>
          </div>
        </div>

        <div class="inst-list">
          {#each installments as row, i}
            <div class="inst-row">
              <span class="inst-badge">#{i + 1}</span>
              <div class="input-wrapper">
                <span class="prefix">HK$</span>
                <input type="number" class="row-input" bind:value={row.amount} />
              </div>
              <input type="date" class="date-input" bind:value={row.dueDate} title="Due Date" />
              {#if installments.length > 1}
                <button class="del-btn" on:click={() => removeInstallmentRow(i)}>✕</button>
              {/if}
            </div>
          {/each}
        </div>

        <div class="balance-indicator" class:valid={isInstallmentSumValid} class:invalid={!isInstallmentSumValid}>
          {#if isInstallmentSumValid}
            <span>{$t('tax.installment_sum_matched', { sum: installmentsSum.toLocaleString() })}</span>
          {:else}
            <span>{$t('tax.installment_diff', { diff: Math.abs(installmentsSum - totalTax).toLocaleString(), reason: installmentsSum > totalTax ? $t('tax.exceeds_tax') : $t('tax.remaining_unallocated') })}</span>
          {/if}
        </div>
      </div>
    {/if}
  </div>

  <div class="card-section">
    <div class="section-header-row">
      <h3>{$t('tax.section4_title')}</h3>
      <label class="switch-container">
        <input type="checkbox" bind:checked={crossPromotion} />
        <span class="switch-label">{$t('tax.cross_promotion')}</span>
      </label>
    </div>

    <div class="promo-selection-bar">
      <span>{$t('tax.promos_selected', { selected: selectedPromoIds.size, total: promotions.length })}</span>
      {#if promotions.length > 0}
        <div class="promo-actions">
          <button class="text-link-btn" on:click={selectAllPromos}>{$t('tax.select_all')}</button>
          <button class="text-link-btn" on:click={clearAllPromos}>{$t('tax.clear')}</button>
        </div>
      {/if}
    </div>

    {#if sameBankWarnings.length > 0}
      <div class="bank-warning-alert">
        {#each sameBankWarnings as warn}
          <div class="bank-warning-row">
            <span class="warn-icon">⚠️</span>
            <span>{warn}</span>
          </div>
        {/each}
      </div>
    {/if}

    {#if isLoadingPromos}
      <p class="loading-text">{$t('tax.loading_promos')}</p>
    {:else if promotions.length === 0}
      <div class="empty-promos-container">
        <div class="empty-icon">📥</div>
        <p class="empty-msg">{$t('tax.empty_promos_msg')}</p>
        <button class="add-promo-btn" on:click={() => navigate('/add')}>
          {$t('tax.add_promo_btn')}
        </button>
      </div>
    {:else}
      <div class="promos-grid">
        {#each promotions as promo (promo.id)}
          <label class="promo-card" class:checked={selectedPromoIds.has(promo.id)}>
            <input
              type="checkbox"
              checked={selectedPromoIds.has(promo.id)}
              on:change={() => togglePromo(promo.id)}
            />
            <div class="promo-info">
              <span class="promo-title">{promo.name || `${promo.brand} Tax Offer`}</span>
              <span class="promo-badge">{promo.brand}</span>
              <span class="promo-desc">{formatPromoSubtitle(promo, $t)}</span>
            </div>
          </label>
        {/each}
      </div>
    {/if}
  </div>

  <button
    class="calc-btn"
    disabled={!canCalculate}
    on:click={handleCalculate}
  >
    {$t('tax.calculate_btn')}
  </button>

  {#if calculationResult}
    <div class="result-anchor">
      <TaxRebateShareCard result={calculationResult} />
    </div>
  {/if}
</div>

<style>
  .calculator-page {
    max-width: 1200px;
    margin: 0 auto;
    padding: 1.5rem 1rem 3rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  .header-banner {
    display: flex;
    align-items: center;
    gap: 16px;
    background: linear-gradient(135deg, #1b5e20, #2e7d32);
    color: white;
    padding: 18px 24px;
    border-radius: 14px;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
    margin-bottom: 2rem;
  }

  .banner-icon {
    font-size: 38px;
    background: rgba(255, 255, 255, 0.15);
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .banner-texts h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
  }

  .banner-texts p {
    margin: 4px 0 0;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.85);
  }

  .card-section {
    background: white;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    padding: 18px 20px;
    margin-bottom: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }

  .card-section h3 {
    margin: 0 0 12px;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.6px;
    color: #002366;
  }

  .section-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .section-header-row h3 {
    margin: 0;
  }

  .switch-container {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    color: #333;
  }

  .preference-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .pref-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 14px;
    background: #f8fafc;
    border: 1.5px solid #cbd5e1;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .pref-btn.active {
    background: rgba(27, 94, 32, 0.08);
    border-color: #1b5e20;
  }

  .pref-btn .icon {
    font-size: 24px;
    margin-bottom: 4px;
  }

  .pref-btn .title {
    font-weight: 800;
    font-size: 14px;
    color: #1e293b;
  }

  .pref-btn.active .title {
    color: #1b5e20;
  }

  .pref-btn .desc {
    font-size: 11px;
    color: #64748b;
    margin-top: 2px;
    text-align: center;
  }

  .input-group {
    display: flex;
    align-items: center;
    border: 2px solid #cbd5e1;
    border-radius: 8px;
    padding: 6px 12px;
    max-width: 400px;
  }

  .input-group:focus-within {
    border-color: #1b5e20;
  }

  .currency-prefix {
    font-size: 18px;
    font-weight: 800;
    color: #1b5e20;
    margin-right: 8px;
    white-space: nowrap;
  }

  .tax-input {
    border: none;
    font-size: 20px;
    font-weight: 800;
    width: 100%;
    outline: none;
    color: #1e293b;
  }

  .preset-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
  }

  .chip-btn {
    background: #f1f5f9;
    border: 1px solid #cbd5e1;
    padding: 5px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
    white-space: nowrap;
  }

  .chip-btn:hover {
    background: #e2e8f0;
  }

  .installments-box {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 14px;
    margin-top: 8px;
    box-sizing: border-box;
    overflow: hidden;
  }

  .inst-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }

  .inst-subtitle {
    font-size: 13px;
    font-weight: 700;
    color: #334155;
  }

  .inst-actions {
    display: flex;
    gap: 6px;
  }

  .small-btn {
    background: white;
    border: 1px solid #cbd5e1;
    padding: 3px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
  }

  .small-btn.add {
    background: #002366;
    color: white;
    border-color: #002366;
  }

  .inst-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    box-sizing: border-box;
  }

  .inst-row {
    display: flex;
    align-items: center;
    gap: 8px;
    background: white;
    padding: 6px 10px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
  }

  .inst-badge {
    background: rgba(0, 35, 102, 0.1);
    color: #002366;
    font-weight: 800;
    font-size: 12px;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .input-wrapper {
    display: flex;
    align-items: center;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    padding: 4px 8px;
    flex: 1;
    min-width: 0;
    box-sizing: border-box;
  }

  .prefix {
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    margin-right: 4px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .row-input {
    border: none;
    outline: none;
    width: 100%;
    font-size: 14px;
    font-weight: 600;
    min-width: 0;
    box-sizing: border-box;
  }

  .date-input {
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 12px;
    color: #334155;
    box-sizing: border-box;
    max-width: 100%;
    min-width: 0;
  }

  .del-btn {
    background: none;
    border: none;
    color: #ef4444;
    font-size: 14px;
    cursor: pointer;
    padding: 4px;
    flex-shrink: 0;
  }

  .balance-indicator {
    margin-top: 10px;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
  }

  .balance-indicator.valid {
    background: #dcfce7;
    color: #166534;
    border: 1px solid #bbf7d0;
  }

  .balance-indicator.invalid {
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
  }

  .promo-selection-bar {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    font-weight: 700;
    color: #475569;
    margin-bottom: 10px;
  }

  .text-link-btn {
    background: none;
    border: none;
    color: #002366;
    font-weight: 700;
    cursor: pointer;
    font-size: 12px;
  }

  .promos-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .promo-card {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    cursor: pointer;
    background: #f8fafc;
    transition: all 0.2s;
  }

  .promo-card.checked {
    background: white;
    border-color: #1b5e20;
    box-shadow: 0 2px 6px rgba(27, 94, 32, 0.1);
  }

  .promo-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .promo-title {
    font-weight: 700;
    font-size: 14px;
    color: #1e293b;
  }

  .promo-badge {
    font-size: 10px;
    font-weight: 800;
    background: rgba(0, 35, 102, 0.1);
    color: #002366;
    display: inline-block;
    padding: 2px 6px;
    border-radius: 4px;
    width: fit-content;
  }

  .promo-desc {
    font-size: 12px;
    color: #64748b;
  }

  .calc-btn {
    width: 100%;
    padding: 16px;
    background: #1b5e20;
    color: white;
    font-size: 16px;
    font-weight: 800;
    letter-spacing: 0.8px;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    transition: background 0.2s;
    box-shadow: 0 4px 12px rgba(27, 94, 32, 0.25);
  }

  .calc-btn:disabled {
    background: #94a3b8;
    cursor: not-allowed;
    box-shadow: none;
  }

  .calc-btn:hover:not(:disabled) {
    background: #144618;
  }

  .result-anchor {
    scroll-margin-top: 20px;
  }

  .empty-promos-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2.5rem 1rem;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 10px;
    text-align: center;
    gap: 12px;
  }

  .empty-icon {
    font-size: 2.2rem;
  }

  .empty-msg {
    color: #1e293b;
    font-size: 14px;
    font-weight: 600;
    max-width: 420px;
    margin: 0;
    line-height: 1.5;
  }

  .add-promo-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 20px;
    background: #1b5e20;
    color: white;
    font-size: 13px;
    font-weight: 700;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s, transform 0.1s;
    box-shadow: 0 2px 6px rgba(27, 94, 32, 0.2);
  }

  .add-promo-btn:hover {
    background: #144618;
    transform: translateY(-1px);
  }

  .bank-warning-alert {
    background: #fff8e1;
    border: 1px solid #ffe082;
    border-left: 4px solid #ffb300;
    padding: 10px 14px;
    border-radius: 8px;
    margin-bottom: 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .bank-warning-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 13px;
    color: #8d6e63;
    line-height: 1.4;
    font-weight: 500;
  }

  .warn-icon {
    font-size: 15px;
    flex-shrink: 0;
  }

  @media (max-width: 640px) {
    .calculator-page {
      padding: 0.5rem 0.25rem 2rem;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }

    .header-banner {
      padding: 12px 14px;
      gap: 12px;
      margin-bottom: 0.75rem;
      border-radius: 10px;
    }

    .banner-icon {
      width: 44px;
      height: 44px;
      font-size: 26px;
      flex-shrink: 0;
    }

    .banner-texts h2 {
      font-size: 17px;
    }

    .banner-texts p {
      font-size: 12px;
    }

    .card-section {
      padding: 12px 10px;
      margin-bottom: 0.75rem;
      border-radius: 10px;
    }

    .card-section h3 {
      margin: 0 0 8px;
      font-size: 12px;
    }

    .section-header-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
      margin-bottom: 8px;
    }

    .preference-grid {
      gap: 8px;
    }

    .pref-btn {
      padding: 10px 8px;
    }

    .preset-chips {
      margin-top: 8px;
      gap: 6px;
    }

    .installments-box {
      padding: 10px 8px;
      margin-top: 6px;
    }

    .inst-controls {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }

    .inst-actions {
      width: 100%;
      justify-content: flex-start;
      flex-wrap: wrap;
      gap: 6px;
    }

    .inst-row {
      display: grid;
      grid-template-columns: auto 1fr auto;
      gap: 6px 8px;
      align-items: center;
      padding: 8px;
    }

    .inst-badge {
      grid-column: 1;
      grid-row: 1;
    }

    .input-wrapper {
      grid-column: 2;
      grid-row: 1;
      width: 100%;
    }

    .del-btn {
      grid-column: 3;
      grid-row: 1;
    }

    .date-input {
      grid-column: 1 / -1;
      grid-row: 2;
      width: 100%;
    }

    .calc-btn {
      padding: 14px;
      font-size: 15px;
      border-radius: 10px;
    }
  }
</style>
